import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { DollarSign, Check, X, Clock, AlertCircle, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MyOffersSection({ token, onCartUpdated }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFinalOfferModal, setShowFinalOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [finalOfferAmount, setFinalOfferAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchOffers = async () => {
    try {
      const res = await axios.get(`${API}/offers/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch offers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchOffers();
    }
  }, [token]);

  // Show toast for pending counter offers or final offers awaiting response
  useEffect(() => {
    const pendingCounters = offers.filter(o => o.status === 'countered' || o.status === 'final_offer');
    if (pendingCounters.length > 0) {
      pendingCounters.forEach(offer => {
        const amount = offer.counter_amount?.toFixed(2);
        const isFinal = offer.status === 'final_offer';
        toast.info(
          `${isFinal ? 'FINAL OFFER' : 'Counter Offer'}: $${amount} on ${offer.product_name}`,
          {
            duration: 8000,
            action: {
              label: 'View',
              onClick: () => {
                document.getElementById('my-offers-section')?.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }
        );
      });
    }
  }, [offers]);

  const handleAcceptCounter = async (offer) => {
    try {
      await axios.put(
        `${API}/offers/${offer.id}/accept-counter`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Accepted! $${offer.counter_amount?.toFixed(2)} - Item added to cart`);
      fetchOffers();
      onCartUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleDecline = async (offer) => {
    if (!window.confirm('Decline this offer?')) return;
    try {
      await axios.put(
        `${API}/offers/${offer.id}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Offer declined');
      fetchOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
  };

  const handleWithdraw = async (offer) => {
    if (!window.confirm('Withdraw this offer?')) return;
    try {
      await axios.delete(
        `${API}/offers/${offer.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Offer withdrawn');
      fetchOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to withdraw');
    }
  };

  const openFinalOfferModal = (offer) => {
    setSelectedOffer(offer);
    // Suggest a value between original offer and counter
    const suggested = ((offer.offer_amount + offer.counter_amount) / 2).toFixed(2);
    setFinalOfferAmount(suggested);
    setShowFinalOfferModal(true);
  };

  const handleSubmitFinalOffer = async () => {
    if (!selectedOffer) return;
    
    const amount = parseFloat(finalOfferAmount);
    if (isNaN(amount)) {
      toast.error('Enter a valid amount');
      return;
    }
    
    if (amount <= selectedOffer.offer_amount) {
      toast.error(`Must be higher than your original offer of $${selectedOffer.offer_amount.toFixed(2)}`);
      return;
    }
    
    if (amount >= selectedOffer.counter_amount) {
      toast.error(`Must be less than the counter of $${selectedOffer.counter_amount.toFixed(2)}`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(
        `${API}/offers/${selectedOffer.id}/final-offer`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Final offer of $${amount.toFixed(2)} submitted!`);
      setShowFinalOfferModal(false);
      setSelectedOffer(null);
      fetchOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <Clock className="w-8 h-8 text-zinc-500 mx-auto animate-spin" />
        <p className="font-mono text-xs text-zinc-500 mt-2">LOADING OFFERS...</p>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="text-center py-8 border border-zinc-800 bg-zinc-900/50">
        <DollarSign className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="font-mono text-sm text-zinc-500">NO ACTIVE OFFERS</p>
        <p className="font-mono text-xs text-zinc-600 mt-1">
          MAKE AN OFFER ON FIXED-PRICE ITEMS TO NEGOTIATE
        </p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'countered': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'final_offer': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'user_final': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'accepted': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'declined': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'declined_final': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'expired': return 'bg-zinc-700/20 text-zinc-500 border-zinc-700/30';
      default: return 'bg-zinc-700/20 text-zinc-400 border-zinc-700/30';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'PENDING REVIEW';
      case 'countered': return 'COUNTER RECEIVED';
      case 'final_offer': return 'FINAL OFFER';
      case 'user_final': return 'YOUR FINAL OFFER - AWAITING RESPONSE';
      case 'accepted': return 'ACCEPTED - CHECK CART';
      case 'declined': return 'DECLINED';
      case 'declined_final': return 'DECLINED';
      case 'withdrawn': return 'WITHDRAWN';
      case 'expired': return 'EXPIRED';
      default: return status.toUpperCase();
    }
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    try {
      const expiry = new Date(expiresAt);
      const now = new Date();
      const diffMs = expiry - now;
      
      if (diffMs <= 0) return 'EXPIRED';
      
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `${hours}H ${minutes}M LEFT`;
      }
      return `${minutes}M LEFT`;
    } catch {
      return null;
    }
  };

  const isActiveStatus = (status) => {
    return ['pending', 'countered', 'final_offer', 'user_final'].includes(status);
  };

  return (
    <div id="my-offers-section" className="space-y-4">
      <h3 className="font-mono text-sm text-zinc-400 border-b border-zinc-800 pb-2">
        MY OFFERS ({offers.length})
      </h3>
      
      {offers.map((offer) => (
        <div 
          key={offer.id}
          className={`border p-4 ${getStatusColor(offer.status)}`}
        >
          <div className="flex gap-3 mb-3">
            {offer.product_image && (
              <img 
                src={offer.product_image.startsWith('data:') ? offer.product_image : 
                     offer.product_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${offer.product_image}` : 
                     offer.product_image}
                alt={offer.product_name}
                className="w-14 h-14 object-cover"
              />
            )}
            <div className="flex-1">
              <p className="font-mono text-sm text-white uppercase">{offer.product_name}</p>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">
                {getStatusLabel(offer.status)}
              </p>
              {/* Show expiration timer for active offers */}
              {isActiveStatus(offer.status) && offer.expires_at && (
                <p className="font-mono text-[10px] text-red-400 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {getTimeRemaining(offer.expires_at)}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3 mb-3 text-center">
            <div>
              <p className="font-mono text-[10px] text-zinc-500">ASKING</p>
              <p className="font-mono text-sm text-zinc-400">${offer.product_price?.toFixed(2)}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] text-zinc-500">YOUR OFFER</p>
              <p className="font-mono text-sm text-yellow-400">${offer.offer_amount?.toFixed(2)}</p>
            </div>
            {offer.counter_amount && (
              <div>
                <p className="font-mono text-[10px] text-zinc-500">THEIR COUNTER</p>
                <p className="font-mono text-sm text-blue-400">${offer.counter_amount?.toFixed(2)}</p>
              </div>
            )}
            {offer.user_final_amount && (
              <div>
                <p className="font-mono text-[10px] text-zinc-500">YOUR FINAL</p>
                <p className="font-mono text-sm text-purple-400">${offer.user_final_amount?.toFixed(2)}</p>
              </div>
            )}
          </div>
          
          {/* Actions based on status */}
          {offer.status === 'pending' && (
            <div className="flex gap-2 pt-3 border-t border-zinc-800/50">
              <button
                onClick={() => handleWithdraw(offer)}
                className="flex items-center gap-1.5 px-2 py-1 bg-zinc-900 border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500 font-mono text-[10px] transition-colors"
              >
                <X className="w-3 h-3" /> WITHDRAW
              </button>
            </div>
          )}
          
          {offer.status === 'countered' && (
            <div className="pt-3 border-t border-zinc-800/50">
              <p className="font-mono text-xs text-zinc-400 mb-2">
                They countered at ${offer.counter_amount?.toFixed(2)}. Your move:
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => handleAcceptCounter(offer)}
                  className="h-8 px-3 rounded-none bg-green-500 text-white hover:bg-green-400 font-mono text-xs"
                >
                  <Check className="w-3 h-3 mr-1" /> ACCEPT ${offer.counter_amount?.toFixed(2)}
                </Button>
                <Button
                  size="sm"
                  onClick={() => openFinalOfferModal(offer)}
                  className="h-8 px-3 rounded-none bg-purple-500 text-white hover:bg-purple-400 font-mono text-xs"
                >
                  <DollarSign className="w-3 h-3 mr-1" /> MY FINAL OFFER
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDecline(offer)}
                  className="h-8 px-3 rounded-none border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono text-xs"
                >
                  <X className="w-3 h-3 mr-1" /> DECLINE
                </Button>
              </div>
            </div>
          )}
          
          {offer.status === 'final_offer' && (
            <div className="pt-3 border-t border-zinc-800/50">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-orange-400" />
                <p className="font-mono text-xs text-orange-400">
                  FINAL OFFER - Accept or decline. No counter allowed.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleAcceptCounter(offer)}
                  className="h-8 px-3 rounded-none bg-green-500 text-white hover:bg-green-400 font-mono text-xs"
                >
                  <Check className="w-3 h-3 mr-1" /> ACCEPT ${offer.counter_amount?.toFixed(2)}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDecline(offer)}
                  className="h-8 px-3 rounded-none border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono text-xs"
                >
                  <X className="w-3 h-3 mr-1" /> DECLINE
                </Button>
              </div>
            </div>
          )}
          
          {offer.status === 'accepted' && (
            <div className="flex items-center gap-2 pt-3 border-t border-zinc-800/50">
              <ShoppingCart className="w-4 h-4 text-green-400" />
              <p className="font-mono text-xs text-green-400">
                Item added to your cart at the agreed price!
              </p>
            </div>
          )}
        </div>
      ))}
      
      {/* Final Offer Modal */}
      <Dialog open={showFinalOfferModal} onOpenChange={setShowFinalOfferModal}>
        <DialogContent className="max-w-sm bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-mono text-lg text-white">
              YOUR FINAL OFFER
            </DialogTitle>
          </DialogHeader>
          
          {selectedOffer && (
            <div className="space-y-4 py-4">
              <p className="font-mono text-xs text-zinc-400">
                Enter an amount between ${selectedOffer.offer_amount?.toFixed(2)} and ${selectedOffer.counter_amount?.toFixed(2)}
              </p>
              
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  step="0.01"
                  value={finalOfferAmount}
                  onChange={(e) => setFinalOfferAmount(e.target.value)}
                  className="pl-7 h-12 bg-zinc-900 border-zinc-700 text-white font-mono text-lg rounded-none"
                />
              </div>
              
              <p className="font-mono text-[10px] text-zinc-500">
                This is your FINAL OFFER. You will not be able to submit offers on the same item if they decline.
              </p>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFinalOfferModal(false)}
              className="flex-1 rounded-none border-zinc-700 font-mono text-xs"
            >
              CANCEL
            </Button>
            <Button
              onClick={handleSubmitFinalOffer}
              disabled={submitting}
              className="flex-1 rounded-none bg-purple-500 hover:bg-purple-400 text-white font-mono text-xs"
            >
              {submitting ? 'SUBMITTING...' : 'SUBMIT FINAL OFFER'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
